import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Turnstile } from '@/components/ui/Turnstile'

const phrases = [
  {
    quote: 'Validea te ayuda a descubrir si tu idea tiene tracción real antes de invertir tiempo y dinero.',
    author: 'Sofía Méndez',
    role: 'Fundadora de StartUpX',
  },
  {
    quote: 'Prioriza las funcionalidades que tus usuarios realmente quieren con datos, no suposiciones.',
    author: 'Pablo Torres',
    role: 'Product Manager',
  },
  {
    quote: 'Valida tu MVP en días, no meses, y toma decisiones basadas en evidencia.',
    author: 'Laura Ruiz',
    role: 'Emprendedora Serial',
  },
]

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY

if (!TURNSTILE_SITE_KEY) {
  console.error('VITE_TURNSTILE_SITE_KEY no está definido en las variables de entorno');
}

export default function Login() {
  const [current, setCurrent] = useState(0)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    console.log('Turnstile Site Key:', TURNSTILE_SITE_KEY);
  }, []);

  const handleTurnstileVerify = (token: string) => {
    console.log('Turnstile verificado:', token.substring(0, 10) + '...');
    setTurnstileToken(token);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    console.log('Intentando enviar formulario:', {
      email,
      turnstileTokenPresente: !!turnstileToken,
      turnstileTokenLength: turnstileToken.length
    });

    if (!turnstileToken) {
      setError('Por favor, completa el captcha.')
      return
    }

    setError('')
    setLoading(true)
    
    try {
      console.log('Enviando solicitud al servidor...');
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email, 
          password,
          turnstileToken 
        })
      })

      const data = await res.json()
      console.log('Respuesta del servidor:', { status: data.status, mensaje: data.message });

      if (data.status === 'success' && data.token && data.data) {
        sessionStorage.setItem('token', data.token)
        sessionStorage.setItem('user', JSON.stringify(data.data))
        navigate('/dashboard')
      } else {
        setError(data.message || 'Credenciales incorrectas.')
      }
    } catch (err) {
      console.error('Error en la solicitud:', err);
      setError('Error de conexión. Por favor, intenta de nuevo.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Columna izquierda */}
      <div className="hidden md:flex flex-col justify-center items-start flex-1 bg-gradient-to-br from-[#6366F1] to-[#4338CA] px-16">
        <h1 className="text-4xl font-extrabold text-white mb-8">Bienvenido a Validea</h1>
        <blockquote className="text-white text-xl font-light mb-6 max-w-lg">“{phrases[current].quote}”</blockquote>
        <div className="mb-2">
          <span className="text-white font-semibold">{phrases[current].author}</span><br />
          <span className="text-white text-sm">{phrases[current].role}</span>
        </div>
        {/* Slider dots */}
        <div className="flex gap-2 mt-6">
          {phrases.map((_, idx) => (
            <button
              key={idx}
              className={`h-3 w-3 rounded-full border-2 ${current === idx ? 'bg-white border-white' : 'bg-transparent border-white opacity-60'} transition-all`}
              onClick={() => setCurrent(idx)}
              aria-label={`Frase ${idx + 1}`}
            />
          ))}
        </div>
      </div>
      {/* Columna derecha */}
      <div className="flex-1 flex items-center justify-center bg-white">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-3xl font-bold text-[#6366F1] text-center mb-2">Validea</h2>
          <h3 className="text-xl font-semibold text-gray-900 text-center mb-1">Inicia sesión en tu cuenta</h3>
          <p className="text-center text-gray-500 text-sm mb-6">
            ¿No tienes una cuenta?{' '}
            <Link to="/register" className="text-[#6366F1] hover:underline">Regístrate aquí</Link>
          </p>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Correo electrónico</label>
              <input
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                required
                placeholder="tu@email.com"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#6366F1] focus:ring-[#6366F1]"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">Contraseña</label>
              <input
                type="password"
                id="password"
                name="password"
                autoComplete="current-password"
                required
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#6366F1] focus:ring-[#6366F1]"
                value={password}
                onChange={e => setPassword(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" className="rounded border-gray-300" disabled={loading} /> Recordarme
              </label>
              <Link to="/forgot-password" className="text-[#6366F1] text-sm hover:underline">¿Olvidaste tu contraseña?</Link>
            </div>

            {/* Turnstile captcha */}
            <div className="mt-4">
              {TURNSTILE_SITE_KEY ? (
                <Turnstile
                  siteKey={TURNSTILE_SITE_KEY}
                  onVerify={handleTurnstileVerify}
                  theme="light"
                  className="flex justify-center"
                />
              ) : (
                <div className="text-red-600 text-sm text-center">
                  Error: No se pudo cargar el captcha. Por favor, contacta al soporte.
                </div>
              )}
            </div>

            {error && <div className="text-red-600 text-sm text-center">{error}</div>}
            <button
              type="submit"
              className="w-full bg-[#6366F1] hover:bg-[#4338CA] text-white font-semibold py-2 rounded-md mt-2 transition-colors disabled:opacity-60"
              disabled={loading || !turnstileToken}
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
} 